import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import logo from '../../Header/Images/RMB-Strip-Jaipur.png';
import scanner from '../../stickySide/images/sacnner.jpg';
import { FaCalendarAlt, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaUserFriends, FaUserCheck, FaLock, FaSignInAlt, FaUsers, FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface EventInfo {
  id: string;
  title: string;
  date: string;
  description?: string;
  location?: string;
}

interface MemberInfo {
  id: number;
  name: string;
  email: string;
  number: number | string;
  company_name?: string;
  designation?: string;
}

interface DynamicField {
  id: number;
  form_type: string;
  field_label: string;
  field_name: string;
  field_type: 'text' | 'email' | 'number' | 'tel' | 'date' | 'textarea' | 'select' | 'file' | 'qr_payment';
  options?: any;
  is_required: number;
  sort_order: number;
}

const EventRsvpPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [event, setEvent] = useState<EventInfo | null>(null);
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [rsvps, setRsvps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Authentication State
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showAttendeeRoster, setShowAttendeeRoster] = useState(false);

  const [activeTab, setActiveTab] = useState<'MEMBER' | 'VISITOR'>('MEMBER');

  // Dynamic Visitor Registration Form Fields & Data
  const [visitorFields, setVisitorFields] = useState<DynamicField[]>([]);
  const [visitorFormData, setVisitorFormData] = useState<Record<string, any>>({});

  // Member RSVP Form State
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [rsvpStatus, setRsvpStatus] = useState<'COMING' | 'CANT_COME' | 'COMING_WITH_VISITOR' | 'SUBSTITUTE'>('COMING');
  
  // Substitute Details
  const [subName, setSubName] = useState('');
  const [subPhone, setSubPhone] = useState('');
  const [subCompany, setSubCompany] = useState('');

  // Bringing Visitor Details
  const [bringingVisName, setBringingVisName] = useState('');
  const [bringingVisPhone, setBringingVisPhone] = useState('');
  const [bringingVisCompany, setBringingVisCompany] = useState('');

  // Independent Visitor Registration Form State
  const [visName, setVisName] = useState('');
  const [visPhone, setVisPhone] = useState('');
  const [visEmail, setVisEmail] = useState('');
  const [visCompany, setVisCompany] = useState('');
  const [visCategory, setVisCategory] = useState('');
  const [invitedBy, setInvitedBy] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');

  // Fetch dynamic fields configured for VISITOR_REGISTRATION
  useEffect(() => {
    const fetchVisitorFields = async () => {
      try {
        const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/fields?form_type=VISITOR_REGISTRATION');
        if (res.data.success) {
          setVisitorFields(res.data.fields || []);
        }
      } catch (err) {
        console.error('Error fetching visitor registration fields:', err);
      }
    };
    fetchVisitorFields();
  }, []);

  // Check logged in member on page load
  useEffect(() => {
    const checkAuth = async () => {
      setCheckingAuth(true);
      try {
        const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/memberLoggedIn', { withCredentials: true });
        if (res.data && res.data.data) {
          setCurrentUser(res.data.data);
          setSelectedMemberId(String(res.data.data.id));
        } else {
          const localUser = localStorage.getItem('impersonatedUser') || localStorage.getItem('user');
          if (localUser) {
            try {
              const pUser = JSON.parse(localUser);
              setCurrentUser(pUser);
              setSelectedMemberId(String(pUser.id));
            } catch {}
          }
        }
      } catch (err) {
        console.log('User not logged in for member RSVP');
        const localUser = localStorage.getItem('impersonatedUser') || localStorage.getItem('user');
        if (localUser) {
          try {
            const pUser = JSON.parse(localUser);
            setCurrentUser(pUser);
            setSelectedMemberId(String(pUser.id));
          } catch {}
        }
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (eventId) {
      fetchEventDetails();
    }
  }, [eventId]);

  useEffect(() => {
    if (event) {
      setVisitorFormData(prev => ({
        ...prev,
        event_id: eventId,
        event_name: event.title,
        event_title: event.title,
        event_date: event.date ? new Date(event.date).toLocaleDateString('en-IN', { dateStyle: 'full' }) : ''
      }));
    }
  }, [event, eventId]);

  const fetchEventDetails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/event-rsvp/details/${eventId}`);
      if (res.data.success) {
        setEvent(res.data.event);
        setMembers(res.data.members || []);
        setRsvps(res.data.rsvps || []);
      }
    } catch (err) {
      console.error('Error fetching event RSVP details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Attendees Counts Calculation
  const memberRsvps = rsvps.filter((r: any) => r.rsvp_status !== 'VISITOR_REGISTERED');
  const comingMembers = memberRsvps.filter((r: any) => r.rsvp_status === 'COMING' || r.rsvp_status === 'COMING_WITH_VISITOR');
  const substituteMembers = memberRsvps.filter((r: any) => r.rsvp_status === 'SUBSTITUTE');
  const visitorList = rsvps.filter((r: any) => r.rsvp_status === 'VISITOR_REGISTERED' || r.visitor_name);

  const totalAttendeesCount = comingMembers.length + substituteMembers.length + visitorList.length;

  const handleMemberRsvpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMemberId) {
      setErrMsg('Please select your name from the RMB Member list.');
      return;
    }

    const selectedM = members.find(m => String(m.id) === selectedMemberId);

    setSubmitting(true);
    setErrMsg('');
    setSuccessMsg('');

    try {
      const payload = {
        event_id: eventId,
        type: 'MEMBER_RSVP',
        user_id: selectedM ? selectedM.id : null,
        member_name: selectedM ? selectedM.name : '',
        rsvp_status: rsvpStatus,
        substitute_name: rsvpStatus === 'SUBSTITUTE' ? subName : null,
        substitute_phone: rsvpStatus === 'SUBSTITUTE' ? subPhone : null,
        substitute_company: rsvpStatus === 'SUBSTITUTE' ? subCompany : null,
        visitor_name: rsvpStatus === 'COMING_WITH_VISITOR' ? bringingVisName : null,
        visitor_phone: rsvpStatus === 'COMING_WITH_VISITOR' ? bringingVisPhone : null,
        visitor_company: rsvpStatus === 'COMING_WITH_VISITOR' ? bringingVisCompany : null
      };

      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/event-rsvp/submit', payload);
      if (res.data.success) {
        setSuccessMsg(res.data.message || 'RSVP Submitted Successfully!');
        fetchEventDetails();
      }
    } catch (err: any) {
      console.error('Error submitting RSVP:', err);
      setErrMsg(err.response?.data?.error || 'Failed to submit RSVP response.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVisitorRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const visitorName = visitorFormData.visitor_name || visitorFormData.full_name || visitorFormData.name || visName;
    const visitorPhone = visitorFormData.visitor_phone || visitorFormData.mobile || visitorFormData.phone || visPhone;
    const visitorEmail = visitorFormData.visitor_email || visitorFormData.email || visEmail;
    const visitorCompany = visitorFormData.visitor_company || visitorFormData.company_name || visitorFormData.company || visCompany;
    const visitorCategory = visitorFormData.visitor_category || visitorFormData.business_category || visitorFormData.category || visCategory;
    const visitorHost = visitorFormData.invited_by || invitedBy;

    if (!visitorName || (!visitorPhone && !visitorEmail)) {
      setErrMsg('Please enter Visitor Full Name and Mobile Number / Email.');
      return;
    }

    setSubmitting(true);
    setErrMsg('');
    setSuccessMsg('');

    try {
      const payloadData = {
        ...visitorFormData,
        event_id: eventId,
        event_name: event?.title || 'RMB Chapter Meeting',
        event_title: event?.title || 'RMB Chapter Meeting',
        event_date: event?.date ? new Date(event.date).toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short' }) : '',
        visitor_name: visitorName,
        visitor_phone: visitorPhone,
        visitor_email: visitorEmail,
        visitor_company: visitorCompany,
        visitor_category: visitorCategory,
        invited_by: visitorHost
      };

      // 1. Submit to Dynamic Forms & Enquiries for Admin Verification
      await axios.post('https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/submit', {
        form_type: 'VISITOR_REGISTRATION',
        data: payloadData
      });

      // 2. Submit to Event RSVPs so visitor shows up in event list & attendee counter
      const rsvpPayload = {
        event_id: eventId,
        type: 'VISITOR_REGISTRATION',
        visitor_name: visitorName,
        visitor_phone: visitorPhone,
        visitor_email: visitorEmail,
        visitor_company: visitorCompany,
        visitor_category: visitorCategory,
        invited_by: visitorHost
      };

      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/event-rsvp/submit', rsvpPayload);
      if (res.data.success) {
        setSuccessMsg(res.data.message || '🎉 Visitor Registration submitted successfully! Sent to Admin for verification.');
        fetchEventDetails();
      }
    } catch (err: any) {
      console.error('Error submitting visitor registration:', err);
      setErrMsg(err.response?.data?.error || 'Failed to register visitor.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderDynamicVisitorField = (field: DynamicField) => {
    const value = visitorFormData[field.field_name] || '';

    const handleChange = (val: any) => {
      setVisitorFormData(prev => ({ ...prev, [field.field_name]: val }));
    };

    if (field.field_type === 'qr_payment') {
      let qrImg = scanner;
      let amountStr = '600';

      try {
        if (field.options) {
          const parsed = typeof field.options === 'string' ? JSON.parse(field.options) : field.options;
          if (parsed.qr_image) qrImg = parsed.qr_image;
          if (parsed.amount) amountStr = parsed.amount;
        }
      } catch {}

      return (
        <div key={field.id} className="mb-4 bg-[#1E2B45] p-4 rounded-2xl border border-amber-500/40 shadow-sm text-left">
          <label className="block text-xs font-bold text-amber-300 mb-2">
            {field.field_label} {field.is_required ? <span className="text-rose-400">*</span> : null}
          </label>
          <div className="flex flex-col sm:flex-row items-center gap-4 bg-[#131E32] p-3 rounded-xl border border-[#2A3B5C]">
            <img src={qrImg} alt="RMB Visitor Payment QR Code Scanner" className="w-36 h-36 object-contain rounded-lg border border-slate-700 p-1 bg-white shadow-sm" />
            <div className="text-xs text-slate-300 space-y-2 flex-1">
              <p className="font-extrabold text-white">Scan QR Code using Paytm / GPay / PhonePe / UPI</p>
              <p className="text-slate-200 font-bold">
                Visitor Registration Fee: <span className="text-amber-400 font-black text-sm">₹{amountStr}/-</span> per attendee
              </p>
              <p className="text-[11px] text-slate-400">Includes Meeting Entrance & Networking High Tea.</p>
              
              <label className="flex items-center gap-2 mt-2 font-bold text-slate-200 bg-[#0A2451] p-2.5 rounded-lg border border-[#2A3B5C] cursor-pointer">
                <input
                  type="checkbox"
                  required={Boolean(field.is_required)}
                  checked={Boolean(visitorFormData[field.field_name])}
                  onChange={(e) => handleChange(e.target.checked)}
                  className="accent-[#FDBC12] w-4 h-4"
                />
                I have completed the QR payment of ₹{amountStr}/-
              </label>
            </div>
          </div>
        </div>
      );
    }

    if (field.field_type === 'select') {
      let optionsList: string[] = [];
      try {
        if (field.options) optionsList = typeof field.options === 'string' ? JSON.parse(field.options) : field.options;
      } catch {
        optionsList = field.options ? (field.options as string).split(',') : [];
      }

      return (
        <div key={field.id} className="mb-3 text-left">
          <label className="block text-xs font-bold text-slate-200 mb-1">
            {field.field_label} {field.is_required ? <span className="text-rose-400">*</span> : null}
          </label>
          <select
            required={Boolean(field.is_required)}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12] cursor-pointer"
          >
            <option value="">-- Select {field.field_label} --</option>
            {optionsList.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    if (field.field_type === 'textarea') {
      return (
        <div key={field.id} className="mb-3 text-left">
          <label className="block text-xs font-bold text-slate-200 mb-1">
            {field.field_label} {field.is_required ? <span className="text-rose-400">*</span> : null}
          </label>
          <textarea
            required={Boolean(field.is_required)}
            value={value}
            rows={3}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={`Enter ${field.field_label}`}
            className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12]"
          />
        </div>
      );
    }

    if (field.field_type === 'file') {
      return (
        <div key={field.id} className="mb-3 text-left">
          <label className="block text-xs font-bold text-slate-200 mb-1">
            {field.field_label} {field.is_required ? <span className="text-rose-400">*</span> : null}
          </label>
          <input
            type="file"
            accept="image/*,.pdf"
            required={Boolean(field.is_required)}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => handleChange(reader.result);
                reader.readAsDataURL(file);
              }
            }}
            className="w-full p-2 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-xs text-slate-300"
          />
        </div>
      );
    }

    return (
      <div key={field.id} className="mb-3 text-left">
        <label className="block text-xs font-bold text-slate-200 mb-1">
          {field.field_label} {field.is_required ? <span className="text-rose-400">*</span> : null}
        </label>
        <input
          type={field.field_type === 'number' || field.field_type === 'tel' ? 'text' : field.field_type}
          required={Boolean(field.is_required)}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={`Enter ${field.field_label}`}
          className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12]"
        />
      </div>
    );
  };

  if (loading || checkingAuth) {
    return (
      <div className="min-h-screen bg-[#0B1220] text-slate-100 flex items-center justify-center p-4">
        <div className="text-center font-bold text-slate-400 animate-pulse">Loading Event & Member Session Details...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1220] text-slate-100 font-sans p-4 sm:p-6 lg:p-10 flex flex-col items-center">
      {/* Top Logo Strip */}
      <div className="bg-white px-6 py-2 rounded-2xl shadow-xl border border-slate-200 mb-8 flex items-center justify-center">
        <img src={logo} alt="Rotary RMB Jaipur Logo" className="h-10 object-contain" />
      </div>

      {/* Main Card Container */}
      <div className="max-w-2xl w-full bg-[#131E32] rounded-3xl border border-[#2A3B5C] shadow-2xl overflow-hidden">
        {/* Event Header Banner */}
        <div className="bg-gradient-to-r from-[#0A2451] via-[#02256D] to-[#1E2B45] p-6 text-center border-b border-[#2A3B5C] space-y-2.5">
          <span className="px-3 py-1 bg-[#FDBC12] text-[#0A2451] font-black text-[11px] rounded-full uppercase tracking-wider">
            📅 Upcoming RMB Chapter Event
          </span>
          <h1 className="text-xl sm:text-2xl font-black text-white">{event?.title || 'RMB Jaipur Event'}</h1>
          
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs font-bold text-slate-300 pt-1">
            <span className="flex items-center gap-1.5 text-amber-300">
              <FaCalendarAlt /> {event?.date ? new Date(event.date).toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short' }) : 'Upcoming Date'}
            </span>
            <span className="flex items-center gap-1.5 text-blue-300">
              <FaMapMarkerAlt /> {event?.location || 'Jaipur, Rajasthan'}
            </span>
          </div>

          {event?.description && (
            <p className="text-xs text-slate-300 pt-1 font-medium max-w-lg mx-auto leading-relaxed">{event.description}</p>
          )}

          {/* Live Attendees Counter Strip */}
          <div className="pt-3 border-t border-white/10 mt-3">
            <div className="bg-[#131E32]/80 p-3 rounded-2xl border border-[#2A3B5C] flex flex-wrap justify-between items-center gap-2 text-xs">
              <div className="flex items-center gap-2 font-black text-amber-300">
                <FaUsers className="text-base" />
                <span>Total Confirmed Attendees: <strong className="text-white text-sm">{totalAttendeesCount}</strong></span>
              </div>
              
              <div className="flex items-center gap-3 text-[11px] font-bold text-slate-300">
                <span className="text-emerald-300">✅ {comingMembers.length} Members</span>
                <span className="text-amber-300">🔄 {substituteMembers.length} Substitutes</span>
                <span className="text-cyan-300">🎟️ {visitorList.length} Visitors</span>
              </div>

              {totalAttendeesCount > 0 && (
                <button
                  onClick={() => setShowAttendeeRoster(!showAttendeeRoster)}
                  className="w-full pt-1.5 text-[11px] text-slate-400 hover:text-amber-300 font-bold flex items-center justify-center gap-1 transition cursor-pointer border-t border-slate-700/40 mt-1"
                >
                  {showAttendeeRoster ? <><FaChevronUp /> Hide Attending Roster</> : <><FaChevronDown /> View Attending Roster ({totalAttendeesCount})</>}
                </button>
              )}
            </div>

            {/* Collapsible Attending Roster List */}
            {showAttendeeRoster && rsvps.length > 0 && (
              <div className="mt-3 p-4 bg-[#0D1525] rounded-2xl border border-[#2A3B5C] text-left space-y-2 text-xs max-h-56 overflow-y-auto">
                <p className="font-extrabold text-amber-300 text-[11px] uppercase tracking-wider mb-2">Confirmed Meeting Attendees:</p>
                {rsvps.map((r: any) => {
                  const isVisitor = r.rsvp_status === 'VISITOR_REGISTERED';
                  return (
                    <div key={r.id} className="p-2.5 bg-[#131E32] rounded-xl border border-[#2A3B5C] flex justify-between items-center">
                      <div>
                        <span className="font-extrabold text-white">{isVisitor ? r.visitor_name : r.member_name}</span>
                        {isVisitor ? (
                          <p className="text-[10px] text-slate-400">🎟️ Visitor • {r.visitor_company || 'Guest'} (Invited by: {r.invited_by || 'Direct'})</p>
                        ) : (
                          <div className="text-[10px] text-slate-400">
                            {r.substitute_name && <span className="text-amber-300 font-bold">🔄 Substitute: {r.substitute_name} ({r.substitute_company || 'N/A'})</span>}
                            {r.visitor_name && <span className="text-cyan-300 font-bold">👥 Visitor: {r.visitor_name} ({r.visitor_company || 'N/A'})</span>}
                          </div>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 rounded-full font-black text-[9px] uppercase ${
                        isVisitor ? 'bg-cyan-950 text-cyan-300 border border-cyan-500/30' :
                        r.rsvp_status === 'COMING' ? 'bg-emerald-950 text-emerald-300 border border-emerald-500/30' :
                        r.rsvp_status === 'CANT_COME' ? 'bg-rose-950 text-rose-300 border border-rose-500/30' :
                        r.rsvp_status === 'COMING_WITH_VISITOR' ? 'bg-blue-950 text-blue-300 border border-blue-500/30' :
                        'bg-amber-950 text-amber-300 border border-amber-500/30'
                      }`}>
                        {isVisitor ? 'Visitor' : r.rsvp_status === 'COMING' ? 'Coming' : r.rsvp_status === 'CANT_COME' ? 'Absent' : r.rsvp_status === 'COMING_WITH_VISITOR' ? 'With Visitor' : 'Substitute'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Tab Selection */}
        <div className="grid grid-cols-2 bg-[#0D1525] border-b border-[#2A3B5C] text-xs font-bold">
          <button
            onClick={() => { setActiveTab('MEMBER'); setSuccessMsg(''); setErrMsg(''); }}
            className={`py-3.5 px-4 text-center transition flex items-center justify-center gap-2 cursor-pointer ${
              activeTab === 'MEMBER'
                ? 'bg-[#131E32] text-[#FDBC12] border-b-2 border-[#FDBC12] font-black'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <FaUserCheck /> Member Attendance RSVP
          </button>
          <button
            onClick={() => { setActiveTab('VISITOR'); setSuccessMsg(''); setErrMsg(''); }}
            className={`py-3.5 px-4 text-center transition flex items-center justify-center gap-2 cursor-pointer ${
              activeTab === 'VISITOR'
                ? 'bg-[#131E32] text-[#FDBC12] border-b-2 border-[#FDBC12] font-black'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <FaUserFriends /> Visitor Pre-Registration
          </button>
        </div>

        {/* Feedback Alerts */}
        {successMsg && (
          <div className="m-6 p-4 bg-emerald-950/80 border border-emerald-500/50 rounded-2xl text-emerald-200 text-xs font-bold text-center flex flex-col items-center gap-2 shadow">
            <FaCheckCircle className="text-emerald-400 text-2xl" />
            <span>{successMsg}</span>
          </div>
        )}

        {errMsg && (
          <div className="m-6 p-4 bg-rose-950/80 border border-rose-500/50 rounded-2xl text-rose-200 text-xs font-bold text-center flex flex-col items-center gap-2 shadow">
            <FaTimesCircle className="text-rose-400 text-2xl" />
            <span>{errMsg}</span>
          </div>
        )}

        {/* Form Body */}
        {!successMsg && (
          <div className="p-6">
            {activeTab === 'MEMBER' ? (
              !currentUser ? (
                /* Member Authentication Requirement Card */
                <div className="p-8 bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] text-center space-y-4 shadow-xl">
                  <div className="w-14 h-14 bg-amber-500/20 text-[#FDBC12] rounded-full flex items-center justify-center mx-auto text-2xl border border-[#FDBC12]/40 shadow">
                    <FaLock />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-white">Member Login Required</h3>
                    <p className="text-xs text-slate-300 mt-1.5 max-w-md mx-auto leading-relaxed">
                      To mark your attendance, specify a substitute, or log a visitor for this chapter event, please log in with your RMB member credentials first.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`)}
                    className="px-6 py-3 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow-lg transition inline-flex items-center gap-2 cursor-pointer transform hover:-translate-y-0.5"
                  >
                    <FaSignInAlt /> Log In to Mark Member Attendance
                  </button>
                </div>
              ) : (
                <form onSubmit={handleMemberRsvpSubmit} className="space-y-5 text-xs">
                  {/* Logged in Member Greeting Banner */}
                  <div className="p-3.5 bg-[#0A2451] rounded-xl border border-emerald-500/40 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2.5">
                      <span className="text-emerald-400 text-lg">✅</span>
                      <div>
                        <p className="font-black text-white">{currentUser.name}</p>
                        <p className="text-[11px] text-slate-300">{currentUser.company_name || 'RMB Chapter Member'} • Logged In</p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 bg-emerald-950 text-emerald-300 border border-emerald-500/40 rounded-full text-[10px] font-black uppercase">
                      Active Member
                    </span>
                  </div>

                  <div>
                    <label className="font-extrabold text-amber-300 block mb-1.5">Select Your Name (RMB Member) *</label>
                    <select
                      value={selectedMemberId}
                      onChange={(e) => setSelectedMemberId(e.target.value)}
                      className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12] cursor-pointer"
                      required
                    >
                      <option value="">-- Choose your name from Member Roster --</option>
                      {members.map(m => (
                        <option key={m.id} value={m.id}>{m.name} ({m.company_name || 'RMB Jaipur'})</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="font-extrabold text-slate-200 block mb-2">Are you attending this meeting? *</label>
                    
                    <div className="space-y-2.5">
                      {/* Option 1: I am Coming */}
                      <label className={`flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer ${
                        rsvpStatus === 'COMING' ? 'bg-[#0A2451] border-[#FDBC12] text-white' : 'bg-[#1E2B45] border-[#2A3B5C] text-slate-300'
                      }`}>
                        <input
                          type="radio"
                          name="rsvp"
                          checked={rsvpStatus === 'COMING'}
                          onChange={() => setRsvpStatus('COMING')}
                          className="accent-[#FDBC12]"
                        />
                        <span className="font-bold">✅ I am Coming</span>
                      </label>

                      {/* Option 2: Sorry I cant Come */}
                      <label className={`flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer ${
                        rsvpStatus === 'CANT_COME' ? 'bg-[#0A2451] border-[#FDBC12] text-white' : 'bg-[#1E2B45] border-[#2A3B5C] text-slate-300'
                      }`}>
                        <input
                          type="radio"
                          name="rsvp"
                          checked={rsvpStatus === 'CANT_COME'}
                          onChange={() => setRsvpStatus('CANT_COME')}
                          className="accent-[#FDBC12]"
                        />
                        <span className="font-bold">❌ Sorry, I can't come</span>
                      </label>

                      {/* Option 3: I am coming with visitor */}
                      <label className={`flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer ${
                        rsvpStatus === 'COMING_WITH_VISITOR' ? 'bg-[#0A2451] border-[#FDBC12] text-white' : 'bg-[#1E2B45] border-[#2A3B5C] text-slate-300'
                      }`}>
                        <input
                          type="radio"
                          name="rsvp"
                          checked={rsvpStatus === 'COMING_WITH_VISITOR'}
                          onChange={() => setRsvpStatus('COMING_WITH_VISITOR')}
                          className="accent-[#FDBC12]"
                        />
                        <span className="font-bold">👥 I am coming with a Visitor</span>
                      </label>

                      {/* Option 4: My substitute will join */}
                      <label className={`flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer ${
                        rsvpStatus === 'SUBSTITUTE' ? 'bg-[#0A2451] border-[#FDBC12] text-white' : 'bg-[#1E2B45] border-[#2A3B5C] text-slate-300'
                      }`}>
                        <input
                          type="radio"
                          name="rsvp"
                          checked={rsvpStatus === 'SUBSTITUTE'}
                          onChange={() => setRsvpStatus('SUBSTITUTE')}
                          className="accent-[#FDBC12]"
                        />
                        <span className="font-bold">🔄 My Substitute will join on my behalf</span>
                      </label>
                    </div>
                  </div>

                  {/* Conditional Fields: Bringing Visitor Details */}
                  {rsvpStatus === 'COMING_WITH_VISITOR' && (
                    <div className="p-4 bg-[#1E2B45] rounded-2xl border border-blue-500/40 space-y-3">
                      <h4 className="font-black text-blue-300 text-xs">🎟️ Visitor Information</h4>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-300 mb-1">Visitor Full Name *</label>
                        <input
                          type="text"
                          value={bringingVisName}
                          onChange={(e) => setBringingVisName(e.target.value)}
                          placeholder="Enter visitor's full name"
                          className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none focus:border-[#FDBC12]"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-300 mb-1">Mobile Number</label>
                          <input
                            type="text"
                            value={bringingVisPhone}
                            onChange={(e) => setBringingVisPhone(e.target.value)}
                            placeholder="Visitor phone number"
                            className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none focus:border-[#FDBC12]"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-slate-300 mb-1">Company / Firm Name</label>
                          <input
                            type="text"
                            value={bringingVisCompany}
                            onChange={(e) => setBringingVisCompany(e.target.value)}
                            placeholder="Visitor company name"
                            className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none focus:border-[#FDBC12]"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Conditional Fields: Substitute Details */}
                  {rsvpStatus === 'SUBSTITUTE' && (
                    <div className="p-4 bg-[#1E2B45] rounded-2xl border border-amber-500/40 space-y-3">
                      <h4 className="font-black text-amber-300 text-xs">🔄 Substitute Information</h4>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-300 mb-1">Substitute Full Name *</label>
                        <input
                          type="text"
                          value={subName}
                          onChange={(e) => setSubName(e.target.value)}
                          placeholder="Enter substitute's full name"
                          className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none focus:border-[#FDBC12]"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-300 mb-1">Mobile Number</label>
                          <input
                            type="text"
                            value={subPhone}
                            onChange={(e) => setSubPhone(e.target.value)}
                            placeholder="Substitute phone number"
                            className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none focus:border-[#FDBC12]"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-slate-300 mb-1">Company / Firm Name</label>
                          <input
                            type="text"
                            value={subCompany}
                            onChange={(e) => setSubCompany(e.target.value)}
                            placeholder="Substitute company name"
                            className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none focus:border-[#FDBC12]"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-sm rounded-xl shadow-lg transition cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? 'Submitting RSVP Response...' : 'Confirm RSVP Response'}
                  </button>
                </form>
              )
            ) : (
              /* Independent Dynamic Visitor Registration Form */
              <form onSubmit={handleVisitorRegisterSubmit} className="space-y-4 text-xs">
                <div className="p-4 bg-[#0A2451] rounded-2xl border border-cyan-500/40 text-slate-200 mb-4 space-y-2">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-[#2A3B5C] pb-2.5">
                    <div>
                      <span className="text-[10px] font-black text-amber-400 uppercase tracking-wider">🎯 Pre-Registering For Event</span>
                      <h4 className="text-base font-black text-white">{event?.title || 'RMB Chapter Meeting'}</h4>
                    </div>
                    <div className="text-xs font-bold text-[#FDBC12] bg-[#131E32] px-3 py-1.5 rounded-xl border border-amber-500/40 shadow">
                      📅 {event?.date ? new Date(event.date).toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short' }) : 'Upcoming Date'}
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-300">
                    Pre-register as a visitor for this RMB chapter meeting. The event title and event date are automatically filled and linked to your visitor registration.
                  </p>
                </div>

                {visitorFields.length > 0 ? (
                  /* Render Admin-Configured Dynamic Fields */
                  <div className="space-y-3">
                    {visitorFields.map(f => renderDynamicVisitorField(f))}
                  </div>
                ) : (
                  /* Fallback Fields if Dynamic Fields not yet loaded */
                  <div className="space-y-4">
                    <div>
                      <label className="font-extrabold text-slate-200 block mb-1">Visitor Full Name *</label>
                      <input
                        type="text"
                        value={visName}
                        onChange={(e) => setVisName(e.target.value)}
                        placeholder="Enter your full name"
                        className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12]"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="font-extrabold text-slate-200 block mb-1">Mobile Number *</label>
                        <input
                          type="text"
                          value={visPhone}
                          onChange={(e) => setVisPhone(e.target.value)}
                          placeholder="Enter 10-digit mobile number"
                          className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12]"
                          required
                        />
                      </div>
                      <div>
                        <label className="font-extrabold text-slate-200 block mb-1">Email Address</label>
                        <input
                          type="email"
                          value={visEmail}
                          onChange={(e) => setVisEmail(e.target.value)}
                          placeholder="Enter email address"
                          className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="font-extrabold text-slate-200 block mb-1">Company / Firm Name</label>
                        <input
                          type="text"
                          value={visCompany}
                          onChange={(e) => setVisCompany(e.target.value)}
                          placeholder="Enter business / firm name"
                          className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12]"
                        />
                      </div>
                      <div>
                        <label className="font-extrabold text-slate-200 block mb-1">Business Category / Industry</label>
                        <input
                          type="text"
                          value={visCategory}
                          onChange={(e) => setVisCategory(e.target.value)}
                          placeholder="e.g. IT Services, Real Estate"
                          className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="font-extrabold text-slate-200 block mb-1">Invited By (RMB Member)</label>
                      <select
                        value={invitedBy}
                        onChange={(e) => setInvitedBy(e.target.value)}
                        className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-[#FDBC12] cursor-pointer"
                      >
                        <option value="">-- Direct Visitor / Select Host Member --</option>
                        {members.map(m => (
                          <option key={m.id} value={m.name}>{m.name} ({m.company_name || 'RMB Jaipur'})</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 mt-4 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-sm rounded-xl shadow-lg transition cursor-pointer disabled:opacity-50"
                >
                  {submitting ? 'Registering Visitor...' : 'Confirm Visitor Registration'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>

      <p className="text-[11px] text-slate-500 font-bold text-center mt-8">
        © Rotary Means Business (RMB Jaipur). Empowering Business Through Rotary.
      </p>
    </div>
  );
};

export default EventRsvpPage;
